import { Dictionary } from 'lodash';
import { useCallback, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { PermissionSet, Role, User } from 'api/models';
import { Project } from 'api/project';
import { AccountSocketData, Action, EventType, ObjectType } from 'api/pushes';
import { ResourceType } from 'api/resource-management/types';
import { useIsCurrentUserCollaborator } from 'hooks/permissions';
import { useAccountWsUpdates } from 'hooks/use-account-ws-updates';
import { ALL_USERS_QUERY_KEY } from 'queries/members';
import { logger } from 'services';
import { fullName } from 'utils/full-name';
import { sortByText } from 'utils/sort-by-text';
import { GroupBy, SchedulerReference } from '../../../types';
import { getProjectsOfUser } from '../../../utils/associated-data';
import {
  resourceIdForProject,
  resourceIdForRole,
  resourceIdForUser,
  resourceIdForUserAsLeaf,
} from '../../../utils/id';
import { useAggregationManager } from '../../aggregation-manager';
import { useAddRemoveResource } from '../../resource-manager';

interface Props {
  schedulerRef: SchedulerReference;
  groupBy: GroupBy;
  projects: Project[];
  userLookup: Dictionary<User>;
  roleLookup: Dictionary<Role>;
  projectLookup: Dictionary<Project>;
}
interface UserSocketData {
  user: User;
  permissionSet: PermissionSet;
}
export const useUsersWSManager = (props: Props): void => {
  const { schedulerRef, groupBy, projects, userLookup } = props;
  const queryClient = useQueryClient();
  const isCollaborator = useIsCurrentUserCollaborator();

  const {
    removeResource,
    addUserResourceToProject,
    addUserResource,
    addRoleResource,
  } = useAddRemoveResource(props);
  const { removeAggregationForResource, getAllocatedMinutesFromAggregation } =
    useAggregationManager();

  const handleUserAdd = useCallback(
    (socketData: AccountSocketData<UserSocketData>) => {
      logger.log('user-add ~ socketData', socketData);
      const {
        data: { user },
      } = socketData;

      queryClient.invalidateQueries(ALL_USERS_QUERY_KEY);
      if (groupBy === GroupBy.Users) {
        addUserResource(
          {
            type: ResourceType.User,
            user,
            role: user.role,
          },
          getProjectsOfUser(projects, user.userId),
          isCollaborator,
        );
        /**
         * Addition of summary is handled as filter event is triggered
         * thus including the newly added resource in LITE call
         */
      } else {
        projects.forEach(({ projectId, teamMembers }) => {
          const isUserPartOfProject = teamMembers
            ?.map(({ userId }) => userId)
            .includes(user.userId);

          if (isUserPartOfProject) {
            addUserResourceToProject(projectId, {
              type: ResourceType.User,
              user,
            });
          }
        });
      }
    },
    [
      addUserResource,
      addUserResourceToProject,
      groupBy,
      isCollaborator,
      projects,
      queryClient,
    ],
  );

  const handleUserUpdate = useCallback(
    async (socketData: AccountSocketData<UserSocketData>) => {
      logger.log('user-update ~ socketData', socketData);
      const {
        data: { user },
      } = socketData;

      queryClient.invalidateQueries(ALL_USERS_QUERY_KEY);
      if (groupBy === GroupBy.Users) {
        const userResource =
          schedulerRef.current.instance.resourceStore.getById(
            resourceIdForUser(user.userId),
          );

        if (userResource == null) {
          return;
        }

        userResource.set('firstName', user.firstName);
        userResource.set('lastName', user.lastName);
        userResource.set('role', user.role);
        userResource.set('photoUrl', user.photoUrl);
      } else {
        const isUserRoleChanged =
          user.role?.roleId !== userLookup[user.userId]?.role?.roleId;

        projects.forEach(async ({ projectId, teamMembers }) => {
          const isUserPartOfProject = teamMembers
            ?.map(({ userId }) => userId)
            .includes(user.userId);

          if (!isUserPartOfProject) {
            return;
          }

          if (isUserRoleChanged) {
            if (user.role == null) {
              return;
            }
            const userResource =
              schedulerRef.current.instance.resourceStore.getById(
                resourceIdForUserAsLeaf({
                  projectId,
                  userId: user.userId,
                  roleId: user.role?.roleId,
                }),
              );

            userResource.set('role', user.role);
            const parentResource =
              schedulerRef.current.instance.resourceStore.getById(
                resourceIdForRole(projectId, user.role?.roleId ?? -1),
              );

            if (parentResource != null) {
              const beforeElement = parentResource.children.find(
                (resource) =>
                  sortByText(
                    fullName({
                      firstName: resource.get('firstName'),
                      lastName: resource.get('lastName'),
                    }),
                    fullName(user),
                  ) === 1,
              );

              parentResource.insertChild(userResource, beforeElement);
            } else {
              const addedResource = addRoleResource(
                ResourceType.User,
                projectId,
                user.role,
                [],
              );

              schedulerRef.current.instance.expand(addedResource.id);
              addedResource.insertChild(userResource);
            }
          } else {
            const userResource =
              schedulerRef.current.instance.resourceStore.getById(
                resourceIdForUserAsLeaf({
                  projectId,
                  userId: user.userId,
                  roleId: user.role?.roleId,
                }),
              );

            if (userResource == null) {
              return;
            }

            userResource.set('firstName', user.firstName);
            userResource.set('lastName', user.lastName);
            userResource.set('role', user.role);
            userResource.set('photoUrl', user.photoUrl);
          }
        });
      }
    },
    [addRoleResource, groupBy, projects, queryClient, schedulerRef, userLookup],
  );

  const handleUserDelete = useCallback(
    (_response: AccountSocketData<UserSocketData>) => {
      logger.log('user-delete ~ socketData', _response);

      const {
        data: { user },
      } = _response;

      queryClient.invalidateQueries(ALL_USERS_QUERY_KEY);
      removeAggregationForResource({
        type: ResourceType.User,
        userId: user.userId,
      });

      if (groupBy === GroupBy.Users) {
        const userResource =
          schedulerRef.current.instance.resourceStore.getById(
            resourceIdForUser(user.userId),
          );

        if (userResource == null) {
          return;
        }
        schedulerRef.current.instance.resourceStore.remove(userResource);
      } else {
        projects.forEach(({ projectId, teamMembers }) => {
          const isUserPartOfProject = teamMembers
            ?.map(({ userId }) => userId)
            .includes(user.userId);

          if (isUserPartOfProject) {
            removeResource(projectId, {
              type: ResourceType.User,
              user,
            });

            const totalAllocation =
              getAllocatedMinutesFromAggregation(projectId);
            const resource =
              schedulerRef.current.instance.resourceStore.getById(
                resourceIdForProject(projectId),
              );

            resource.set(
              'totalAllocation',
              totalAllocation.totalAllocatedMinutes,
            );
          }
        });
      }
    },
    [
      getAllocatedMinutesFromAggregation,
      groupBy,
      projects,
      queryClient,
      removeAggregationForResource,
      removeResource,
      schedulerRef,
    ],
  );

  const addHandlerRef = useRef<any>();
  const updateHandlerRef = useRef<any>();
  const deleteHandlerRef = useRef<any>();

  addHandlerRef.current = handleUserAdd;
  updateHandlerRef.current = handleUserUpdate;
  deleteHandlerRef.current = handleUserDelete;

  const addHandlerProxy = useCallback((params) => {
    addHandlerRef.current?.(params);
  }, []);
  const updateHandlerProxy = useCallback((params) => {
    updateHandlerRef.current?.(params);
  }, []);
  const deleteHandlerProxy = useCallback((params) => {
    deleteHandlerRef.current?.(params);
  }, []);

  useAccountWsUpdates(
    addHandlerProxy,
    EventType(Action.Create, ObjectType.AccountMembers),
  );
  useAccountWsUpdates(
    updateHandlerProxy,
    EventType(Action.Update, ObjectType.AccountMembers),
  );
  useAccountWsUpdates(
    deleteHandlerProxy,
    EventType(Action.Delete, ObjectType.AccountMembers),
  );
};
